import { useRef } from 'react'
import { Download, FileDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useReportBrand } from '@/lib/pdf'
import {
  BalanceSheetReport,
  CashFlowReport,
  IncomeStatementReport,
  TrialBalanceReport,
} from '@/features/reporting/reports'
import type {
  BalanceSheetResponse,
  CashFlowResponse,
  IncomeStatementResponse,
  TrialBalanceResponse,
} from '@/features/reporting/types'
import type { ChatFinancialReportPart } from '../types'
import { ShareButton } from './actionBar'

interface Props {
  part: ChatFinancialReportPart
  onShare?: () => void
}

const TITLES: Record<ChatFinancialReportPart['reportType'], string> = {
  balance_sheet: 'Balance Sheet',
  income_statement: 'Income Statement',
  cash_flow: 'Statement of Cash Flows',
  trial_balance: 'Trial Balance',
}

const FILENAMES: Record<ChatFinancialReportPart['reportType'], string> = {
  balance_sheet: 'balance_sheet',
  income_statement: 'income_statement',
  cash_flow: 'cash_flow',
  trial_balance: 'trial_balance',
}

export function FinancialReportBlock({ part, onShare }: Props) {
  const tableRef = useRef<HTMLTableElement | null>(null)
  const reportTitle = part.title || TITLES[part.reportType]
  const brand = useReportBrand()

  async function downloadPdf() {
    if (!tableRef.current) return
    const mod = await import('@/features/reporting/export')
    // For chatbot blocks we have the typed payload, so prefer the
    // data-driven exporters (with proper section bars + totals) over the
    // table-scrape fallback - much more presentable.
    if (part.reportType === 'balance_sheet') {
      mod.exportBalanceSheetPdf(part.data as BalanceSheetResponse, brand)
    } else if (part.reportType === 'income_statement') {
      mod.exportIncomeStatementPdf(part.data as IncomeStatementResponse, brand)
    } else if (part.reportType === 'cash_flow') {
      mod.exportCashFlowPdf(part.data as CashFlowResponse, brand)
    } else if (part.reportType === 'trial_balance') {
      mod.exportTrialBalancePdf(part.data as TrialBalanceResponse, brand)
    } else {
      mod.downloadPdfFromTable(tableRef.current, reportTitle, brand)
    }
  }
  async function downloadCsv() {
    if (!tableRef.current) return
    const mod = await import('@/features/reporting/export')
    mod.downloadCsvFromTable(
      tableRef.current,
      `${FILENAMES[part.reportType]}.csv`,
      brand,
      reportTitle,
    )
  }

  let body: React.ReactNode = null
  if (part.reportType === 'balance_sheet') {
    body = <BalanceSheetReport data={part.data as BalanceSheetResponse} tableRef={tableRef} />
  } else if (part.reportType === 'income_statement') {
    body = <IncomeStatementReport data={part.data as IncomeStatementResponse} tableRef={tableRef} />
  } else if (part.reportType === 'cash_flow') {
    body = <CashFlowReport data={part.data as CashFlowResponse} tableRef={tableRef} />
  } else if (part.reportType === 'trial_balance') {
    body = <TrialBalanceReport data={part.data as TrialBalanceResponse} tableRef={tableRef} />
  }

  return (
    <div className="my-2 flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {reportTitle}
        </p>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => void downloadPdf()}
            className="size-7"
            title="Download PDF"
            aria-label="Download PDF"
          >
            <FileDown className="size-3.5" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => void downloadCsv()}
            className="size-7"
            title="Download CSV"
            aria-label="Download CSV"
          >
            <Download className="size-3.5" />
          </Button>
          <ShareButton onClick={onShare} />
        </div>
      </div>
      {body}
    </div>
  )
}
